import { BillingInterval } from "@flowstepdesign/shared";
import { ClientOnly } from "@tanstack/react-router";
import { useState, useEffect, Suspense, lazy } from "react";

import { BillingToggle } from "../components/BillingToggle";
import { EnterpriseCard } from "../components/EnterpriseCard";
import FallbackBackground from "../components/FallbackBackground";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { PlanCard, type PlanFeature } from "../components/PlanCard";
import { PricingFAQ } from "../components/PricingFAQ";
import { PrimaryCTA } from "../components/PrimaryCTA";
import Testimonials from "../components/Testimonials";
import { TierSelector, type TierOption } from "../components/TierSelector";
import { useNavigateToLink } from "../hooks/useNavigateToLink";
import { ROUTES } from "../routes";

// The static FallbackBackground below is always rendered behind the canvas, so
// if a deploy purged this chunk we degrade to nothing here rather than letting
// the rejected import bubble to the route error boundary and crash the page.
const ScrollReactiveBackground = lazy(() =>
  import("../components/ScrollReactiveBackground").catch(() => ({
    default: () => <></>,
  })),
);

export default function Pricing() {
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("month");
  const currency = "USD";
  const [showMobileFooter, setShowMobileFooter] = useState(false);

  // TODO : ideally we should pull these from server to keep in sync
  // Tier selection state
  const paidTiers: TierOption[] = [
    {
      id: "starter",
      name: "Starter",
      messages: 80,
      monthlyPrice: 15,
      annualPrice: 12,
      persona: "For individuals",
    },
    {
      id: "pro",
      name: "Pro",
      messages: 240,
      monthlyPrice: 29,
      annualPrice: 23,
      multiplier: "3x usage limits",
      persona: "For professionals",
    },
    {
      id: "max",
      name: "Max",
      messages: 1000,
      monthlyPrice: 99,
      annualPrice: 80,
      multiplier: "12x usage limits",
      persona: "For power users",
    },
  ];

  const [selectedTier, setSelectedTier] = useState<TierOption>(paidTiers[0]);

  // Free plan features
  const freePlanFeatures: PlanFeature[] = [
    { label: "Messages · Limited", entitlement: "messages_per_month" },
    { label: "Multi-screen · Limited", entitlement: "multi_screen_per_day" },
    { label: "Projects · Limited", entitlement: "projects_max" },
  ];

  // Paid plan features (dynamically updated based on selected tier)
  const getPaidPlanFeatures = (tier: TierOption): PlanFeature[] => [
    {
      label: `Messages · ${tier.messages}/mo`,
      entitlement: "messages_per_month",
    },
    { label: "Multi-screen · Unlimited", entitlement: "multi_screen_per_day" },
    { label: "Projects · Unlimited", entitlement: "projects_max" },
    { label: "Exports · Unlimited", entitlement: "exports_per_month" },
    {
      label: "Copy to Figma · Unlimited",
      entitlement: "copy_to_figma_per_month",
    },
  ];

  // Load billing preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("flowstep-billing");
    if (saved === "year" || saved === "month") {
      setBillingInterval(saved);
    }
  }, []);

  // Save billing preference to localStorage
  const handleBillingChange = (value: BillingInterval) => {
    setBillingInterval(value);
    localStorage.setItem("flowstep-billing", value);
  };

  // Detect mobile scroll for sticky footer
  useEffect(() => {
    const handleScroll = () => {
      setShowMobileFooter(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initialize on mount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getCurrencySymbol = (code: string) => {
    const symbols: Record<string, string> = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
    };
    return symbols[code] || "$";
  };

  const navigateToLink = useNavigateToLink();

  const handleCtaClick = (e: React.MouseEvent, planId: string) => {
    navigateToLink(e, ROUTES.registerWithPlan(planId, billingInterval));
  };

  return (
    <div className="relative min-h-screen">
      {/* Static gradient — paints immediately on SSR, then the WebGL canvas
          layers on top once its chunk has loaded. */}
      <FallbackBackground variant="dark" />

      {/* Shader-based Background System — client-only, lazy-loaded */}
      <ClientOnly>
        <Suspense fallback={null}>
          <ScrollReactiveBackground variant="dark" />
        </Suspense>
      </ClientOnly>

      {/* Content wrapper with relative positioning */}
      <div className="relative z-10 pb-16 lg:pb-0">
        {/* Navigation Header — dark-scoped to match the dark waves */}
        <div className="dark text-foreground">
          <Header />
        </div>
        {/* Hack: lower everything by the size of header */}
        <div className="h-16" />

        {/* Hero Section */}
        <section className="container mx-auto max-w-7xl px-4 py-8 md:py-16">
          <div className="space-y-8 text-center">
            {/* Main Headline */}
            <div className="space-y-4">
              <h1
                className="mb-6 leading-[0.95] tracking-tight text-white"
                style={{
                  fontSize: "clamp(2.5rem, 8vw, 6rem)",
                  fontWeight: 600,
                }}
              >
                Pricing
              </h1>
              <p
                className="mx-auto max-w-2xl leading-relaxed text-gray-300"
                style={{ fontSize: "clamp(1.125rem, 2.5vw, 1.5rem)" }}
              >
                Start for free. Get design superpowers.
              </p>
            </div>

            {/* Billing Toggle & Currency */}
            <div className="flex flex-col items-center gap-4">
              <BillingToggle
                value={billingInterval}
                onChange={handleBillingChange}
              />
            </div>
          </div>
        </section>

        {/* Plan Cards */}
        <section className="container mx-auto max-w-7xl px-4 pb-16">
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
            {/* Free Plan */}
            <PlanCard
              name="Free"
              persona="For trying out Flowstep"
              monthlyPrice={0}
              annualPrice={0}
              billingInterval={billingInterval}
              features={freePlanFeatures}
              ctaLabel="Start Free"
              onCtaClick={(e) => handleCtaClick(e, "free")}
              planId="free"
              currency={getCurrencySymbol(currency)}
            />

            {/* Combined Paid Plan with Tier Selector */}
            <div className="bg-card relative flex flex-col rounded-2xl border-2 border-[#0247FE] p-8 shadow-sm transition-shadow hover:shadow-md">
              {/* Most popular badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2"></div>

              <div className="flex h-full flex-col space-y-6">
                {/* Header */}
                <div>
                  <h3 className="mb-1 font-semibold text-gray-900">
                    {selectedTier.name}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {selectedTier.persona}
                  </p>
                </div>

                {/* Price */}
                <div>
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl">
                        {getCurrencySymbol(currency)}
                        {billingInterval === "month"
                          ? selectedTier.monthlyPrice
                          : selectedTier.annualPrice}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    {billingInterval === "year" && (
                      <span className="text-muted-foreground text-xs">
                        Billed annually
                      </span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="flex-1 space-y-3">
                  <ul className="space-y-3">
                    {getPaidPlanFeatures(selectedTier).map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <svg
                          className="mt-0.5 size-4 shrink-0 text-[#0247FE]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span
                          className="text-sm"
                          data-entitlement={feature.entitlement}
                        >
                          {feature.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tier Selector */}
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs">Select tier:</p>
                  <TierSelector
                    tiers={paidTiers}
                    selectedTier={selectedTier}
                    onSelectTier={setSelectedTier}
                  />
                </div>

                {/* CTA */}
                <div>
                  <PrimaryCTA
                    className="w-full"
                    onClick={(e) => handleCtaClick(e, selectedTier.id)}
                    data-event="Checkout Started"
                    data-plan={selectedTier.id}
                    data-billing={billingInterval}
                    data-price={`${getCurrencySymbol(currency)}${billingInterval === "month" ? selectedTier.monthlyPrice : selectedTier.annualPrice}`}
                  >
                    Get {selectedTier.name}
                  </PrimaryCTA>
                </div>
              </div>
            </div>
          </div>

          {/* Enterprise Card */}
          <div className="mx-auto mt-6 max-w-3xl">
            <EnterpriseCard
              onBookCall={(e) => navigateToLink(e, ROUTES.bookACall)}
            />
          </div>
        </section>

        {/* Testimonials */}
        <div className="dark text-foreground">
          <Testimonials />
        </div>

        {/* FAQ Section */}
        <div className="dark text-foreground">
          <PricingFAQ />
        </div>

        {/* Footer */}
        <div className="dark text-foreground">
          <Footer />
        </div>

        {/* Sticky Mobile Footer */}
        {showMobileFooter && (
          <div className="dark bg-background text-foreground fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg lg:hidden">
            <div className="flex items-center justify-between gap-4">
              <div className="text-left">
                <p>
                  {selectedTier.name} · {getCurrencySymbol(currency)}
                  {billingInterval === "month"
                    ? selectedTier.monthlyPrice
                    : selectedTier.annualPrice}
                  /mo
                </p>
              </div>
              <PrimaryCTA onClick={(e) => handleCtaClick(e, selectedTier.id)}>
                Get {selectedTier.name}
              </PrimaryCTA>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
